/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React from "react";

import { SalesDashboardPrimaryButton } from "./SalesDashboardPrimaryButton";
import { useExportPurchaseDashboardRowsFile } from "./useExportPurchaseDashboardRowsFile";

export const AdminSalesDashboardHeader: React.FC<{
  eventNames: string[];
  fetchedAt: string;
  eventIds: string[];
  children?: React.ReactNode;
}> = ({ eventNames, fetchedAt, eventIds, children }) => {
  const {
    exportFile,
    isExporting,
    error: exportError,
  } = useExportPurchaseDashboardRowsFile();

  const visibleEventNames = eventNames.slice(0, 2);
  const hiddenEventNamesCount = eventNames.length - visibleEventNames.length;
  const allEventNamesText = eventNames.join(", ");

  return (
    <header css={styles.header}>
      {visibleEventNames.length > 0 ? (
        <div css={styles.eventNames}>
          {visibleEventNames.join(", ")}
          {hiddenEventNamesCount > 0 ? (
            <div
              css={styles.hiddenEventNames}
              tabIndex={0}
              // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
              aria-label={`すべてのイベント名: ${allEventNamesText}`}
            >
              +{hiddenEventNamesCount}
              <ul css={styles.eventNamesTooltip} aria-hidden="true">
                {eventNames.map((eventName) => (
                  <li key={eventName} css={styles.eventNameTooltipItem}>
                    {eventName}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      <div css={styles.titleRow}>
        {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
        <h1 css={styles.title}>チケット売上ダッシュボード</h1>
        <div css={styles.actionArea}>
          {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
          <p css={styles.fetchedAt}>更新日時 {fetchedAt}</p>
          <div css={styles.exportWrapper}>
            <SalesDashboardPrimaryButton
              onClick={() => {
                exportFile(eventIds);
              }}
              disabled={isExporting}
            >
              {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
              {isExporting ? "出力中..." : "Excel出力"}
            </SalesDashboardPrimaryButton>
            {exportError && (
              // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
              <span css={styles.exportError}>エラーが発生しました</span>
            )}
          </div>
          {children}
        </div>
      </div>
    </header>
  );
};

const styles = {
  header: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: #ffffff;
  `,
  eventNames: css`
    position: relative;
    color: #00c2ae;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1.5px;
    line-height: 1.25;
  `,
  hiddenEventNames: css`
    display: inline-flex;
    padding: 1px 6px;
    margin-left: 6px;
    color: #00c2ae;
    background: rgba(0, 194, 174, 0.08);
    border: 1px solid rgba(0, 194, 174, 0.45);
    border-radius: 999px;
    cursor: default;
    transition:
      background 120ms ease,
      border-color 120ms ease;
    &:hover {
      background: rgba(0, 194, 174, 0.16);
      border-color: rgba(0, 194, 174, 0.8);
    }
    &:hover > ul,
    &:focus-visible > ul {
      visibility: visible;
      opacity: 1;
    }
    &:focus-visible {
      border-radius: 4px;
      box-shadow: 0 0 0 2px rgba(0, 194, 174, 0.45);
    }
  `,
  eventNamesTooltip: css`
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: min(520px, 100%);
    max-height: 240px;
    padding: 10px 12px;
    margin: 0;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 1.5;
    white-space: normal;
    word-break: break-word;
    overflow-y: auto;
    background: #111f2d;
    border: 1px solid #2f4052;
    border-radius: 6px;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.32);
    visibility: hidden;
    opacity: 0;
    transition:
      opacity 120ms ease,
      visibility 120ms ease;
    z-index: 1;
  `,
  eventNameTooltipItem: css`
    list-style-position: inside;
  `,
  titleRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    /**
     * HTML構造を変えずに配置だけを切り替えるため、PCView / SPViewではなくメディアクエリで制御する。
     * 表示要素や構造を出し分ける場合は、PCView / SPViewの利用を検討する。
     */
    @media screen and (max-width: 759px) {
      flex-direction: column;
      align-items: stretch;
    }
  `,
  title: css`
    color: #ffffff;
    font-size: clamp(20px, 4.5vw, 26px);
    font-weight: 800;
    letter-spacing: 0.6px;
    line-height: 1.25;
  `,
  actionArea: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
  `,
  fetchedAt: css`
    color: #8b96a2;
    font-size: 14px;
    letter-spacing: 0.3px;
  `,
  exportWrapper: css`
    display: flex;
    align-items: center;
  `,
  exportError: css`
    color: red;
    margin-left: 10px;
  `,
};
